import React from 'react';

import Link from '@/components/Link';
import { getInitials } from '@/utils/getInitials';
import { stringToColor } from '@/utils/stringToColor';

import type { CandidateExpert } from './myProjects.types';

interface ExpertRowProps {
  expert: CandidateExpert;
  expertRequestId: string;
}

const STATE_LABELS: Record<string, string> = {
  suggested_by_research: 'Suggested',
  suggested_by_platform: 'Suggested',
  vetting: 'Vetting',
  verified: 'Verified',
  matched: 'Matched',
};

const STATE_STYLES: Record<string, string> = {
  suggested_by_research: 'bg-surface-secondary text-on-surface-secondary',
  suggested_by_platform: 'bg-surface-secondary text-on-surface-secondary',
  vetting: 'bg-yellow-500/15 text-yellow-800 dark:text-yellow-300',
  verified: 'bg-green-500/15 text-green-700 dark:text-green-300',
  matched: 'bg-brand-light text-brand-light-ink',
};

function getActionForExpert(
  expert: CandidateExpert,
  expertRequestId: string
): { label: string; style: string; to: string } | null {
  const reviewUrl = `/expert_request/${expertRequestId}?candidate_id=${expert.id}`;
  switch (expert.state) {
    case 'verified':
      return { label: 'Review Profile', style: 'bg-yellow-500 text-white', to: reviewUrl };
    case 'suggested_by_research':
    case 'suggested_by_platform':
      return { label: 'Give Feedback', style: 'bg-yellow-500 text-white', to: reviewUrl };
    case 'matched':
      return {
        label: 'Book Call',
        style: 'border border-brand-primary text-brand-primary bg-surface-elevated',
        to: `${expert.htmlUrl}/request?expertRequestId=${expertRequestId}`,
      };
    default:
      return null;
  }
}

export function ExpertRow({ expert, expertRequestId }: ExpertRowProps): React.ReactNode {
  const initials = getInitials(expert.firstName, expert.lastName);
  const bgColor = stringToColor(expert.name, 700);
  const chipLabel = STATE_LABELS[expert.state] ?? expert.state;
  const chipStyle = STATE_STYLES[expert.state] ?? 'bg-surface-secondary text-on-surface-secondary';
  const action = getActionForExpert(expert, expertRequestId);

  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-4 rounded px-8 py-6 hover:bg-surface-elevated">
      <div
        className="flex h-[2.8rem] w-[2.8rem] shrink-0 items-center justify-center rounded-full text-[1rem] font-semibold text-white"
        style={{ backgroundColor: bgColor }}
        aria-hidden="true"
      >
        {initials}
      </div>

      <div className="min-w-0 flex-1 max-lg:basis-full">
        <Link
          to={`${expert.htmlUrl}/request?expertRequestId=${expertRequestId}`}
          className="block truncate text-[1.3rem] font-medium text-on-surface no-underline hover:underline"
        >
          {expert.name}
        </Link>
        {expert.title && (
          <p className="m-0 truncate text-12 text-on-surface-muted">{expert.title}</p>
        )}
      </div>

      <span
        className={`inline-flex shrink-0 items-center rounded px-8 py-2 text-[1.1rem] font-medium ${chipStyle}`}
      >
        {chipLabel}
      </span>

      {action && (
        <Link
          to={action.to}
          className={`inline-flex shrink-0 items-center rounded-md px-10 py-4 text-[1.1rem] font-semibold no-underline ${action.style}`}
        >
          {action.label}
        </Link>
      )}
    </div>
  );
}
